import React, { FunctionComponent } from 'react';
import { Popover, usePopover } from '@trello/nachos';
import classnames from 'classnames';
import styles from './CheckItemOverflowMenu.less';
import { Analytics } from '@trello/atlassian-analytics';
import { forNamespace, forTemplate } from 'app/common/lib/util/i18n';
import { Key, getKey } from '@trello/keybindings';
import { ChecklistTestIds, TestId } from '@trello/test-ids';

const formatButtons = forTemplate('check_item_menu');
const formatTitle = forNamespace('view title');

interface CheckItemOverflowMenuProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  model: any; //temporary hold since backbone model is passed in
  editableView?: boolean;
  testid?: TestId;
}

export const CheckItemOverflowMenu: FunctionComponent<CheckItemOverflowMenuProps> = ({
  model,
  editableView,
}) => {
  //Use Anchor Element for Easier Styling Compatibility with Old Stack.
  const { triggerRef, toggle, hide, popoverProps } = usePopover<
    HTMLAnchorElement
  >();

  const convertToCard = () => {
    model.run('convertToCard');
    model.getChecklist().checkItemList.remove(model);

    Analytics.sendUIEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'convertCheckItemToCardButton',
      source: 'checkItemMenuInlineDialog',
    });

    hide();

    return;
  };

  const deleteCheckitem = () => {
    model.destroy();

    Analytics.sendUIEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'deleteCheckItemButton',
      source: 'checkItemMenuInlineDialog',
    });

    hide();

    return;
  };

  const togglePopover = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    Analytics.sendUIEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'checkItemMenuButton',
      source: 'cardDetailScreen',
    });

    toggle();
  };

  return (
    <>
      <a
        className={
          editableView ? styles.editableViewLink : styles.inlineEditViewLink
        }
        ref={triggerRef}
        onClick={togglePopover}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (getKey(e) === Key.Enter) {
            e.preventDefault();
            toggle();
          }
        }}
        data-test-id={ChecklistTestIds.ChecklistItemOverflowMenuButton}
      >
        <span className="icon-sm icon-overflow-menu-horizontal" />
      </a>
      <Popover
        {...popoverProps}
        title={formatTitle('check item')}
        noHorizontalPadding
        noVerticalPadding
      >
        <button
          className={classnames('convertButton', styles.overflowMenuButton)}
          onClick={convertToCard}
          tabIndex={0}
          data-test-id={ChecklistTestIds.ChecklistItemConvertButton}
        >
          {formatButtons('convert-to-card')}
        </button>
        <button
          className={classnames('deleteButton', styles.overflowMenuButton)}
          onClick={deleteCheckitem}
          tabIndex={0}
          data-test-id={ChecklistTestIds.ChecklistItemDeleteButton}
        >
          {formatButtons('delete')}
        </button>
      </Popover>
    </>
  );
};
